

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  transformSuccessCriteriaToCSV,
  transformSummaryToCSV,
  transformRubricsToCSV,
  transformRubricsToPDF,
  transformShortAnswerToCSV,
} from './dataTransformers';

const MARGIN = 10;
const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm


function sanitizeText(text) {
      // Handle null, undefined, but allow 0, false, and other falsy values
  if (text === null || text === undefined) return '';

  
  const str = String(text);

  const withoutTags = str.replace(/<[^>]*>/g, '');

  const withoutEntities = withoutTags
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');

  const cleaned = withoutEntities
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');

  return cleaned.trim();
}

/**
 * Sanitize an array of text strings
 */
function sanitizeTextArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map(item => sanitizeText(item)).filter(Boolean);
}

/**
 * Add header to PDF with class name and section title
 */
function addHeader(pdf, className, sectionTitle, showMainTitle = true) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  let startY = 15;

  if (showMainTitle) {
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text('Class Insights Report', pageWidth / 2, startY, { align: 'center' });
    startY += 7;
  }

  if (className) {
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    const sanitizedClassName = sanitizeText(className);
    pdf.text(`Class: ${sanitizedClassName}`, pageWidth / 2, startY, { align: 'center' });
    startY += 8;
  }

  if (sectionTitle) {
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    const sanitizedTitle = sanitizeText(sectionTitle);
    pdf.text(sanitizedTitle, MARGIN, startY);
    startY += 5;
  }

  return startY;
}


function csvToTableData(csvData) {
  if (!csvData || csvData.length === 0) {
    return { head: [], body: [] };
  }

  if (typeof csvData[0] === 'object' && !Array.isArray(csvData[0])) {
    const headers = Object.keys(csvData[0]).map(h => sanitizeText(h));
    const head = [headers];

    const body = csvData.map(row =>
      headers.map((_, idx) => {
        const originalHeader = Object.keys(csvData[0])[idx];
        const value = row[originalHeader];
        return sanitizeText(value !== null && value !== undefined ? value : '');
      })
    );

    return { head, body };
  }

  const head = [csvData[0].map(h => sanitizeText(h))];
  const body = csvData.slice(1).map(row =>
    row.map(cell => {
      return sanitizeText(cell !== null && cell !== undefined ? cell : '');
    })
  );

  return { head, body };
}

/**
 * Draw a success criteria progress bar with percentages and percentages
 */
function drawSuccessCriteriaBar(pdf, x, y, width, satisfied, partiallySatisfied, notSatisfied) {
  const barHeight = 8;
  const total = satisfied + partiallySatisfied + notSatisfied;

  if (total === 0) {
    pdf.setFillColor(240, 240, 240);
    pdf.rect(x, y, width, barHeight, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    pdf.text('No assessments yet', x + width / 2, y + barHeight / 2 + 1.5, { align: 'center' });
    return;
  }

  const satisfiedPct = (satisfied / total) * 100;
  const partiallyPct = (partiallySatisfied / total) * 100;
  const notSatisfiedPct = (notSatisfied / total) * 100;

  const satisfiedWidth = (satisfiedPct / 100) * width;
  const partiallyWidth = (partiallyPct / 100) * width;
  const notSatisfiedWidth = (notSatisfiedPct / 100) * width;

  let currentX = x;

  if (satisfiedWidth > 0) {
    pdf.setFillColor(27, 168, 15);
    pdf.rect(currentX, y, satisfiedWidth, barHeight, 'F');

    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');
    const text = `${satisfiedPct.toFixed(1)}%`;
    pdf.text(text, currentX + satisfiedWidth / 2, y + barHeight / 2 + 1.5, { align: 'center' });

    currentX += satisfiedWidth;
  }

  if (partiallyWidth > 0) {
    pdf.setFillColor(228, 119, 10);
    pdf.rect(currentX, y, partiallyWidth, barHeight, 'F');

    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');
    const text = `${partiallyPct.toFixed(1)}%`;
    pdf.text(text, currentX + partiallyWidth / 2, y + barHeight / 2 + 1.5, { align: 'center' });

    currentX += partiallyWidth;
  }

  if (notSatisfiedWidth > 0) {
    pdf.setFillColor(240, 54, 54);
    pdf.rect(currentX, y, notSatisfiedWidth, barHeight, 'F');

    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(undefined, 'bold');
    const text = `${notSatisfiedPct.toFixed(1)}%`;
    pdf.text(text, currentX + notSatisfiedWidth / 2, y + barHeight / 2 + 1.5, { align: 'center' });
  }
}

function drawDistributionBars(pdf, x, y, width, items, colors = []) {
  const safeItems = Array.isArray(items) ? items.filter((item) => Number(item?.value) > 0) : [];
  if (safeItems.length === 0) return y;

  const maxValue = Math.max(...safeItems.map((item) => Number(item.value || 0)), 1);
  const barHeight = 6;
  const rowGap = 9;
  const labelWidth = Math.min(70, width * 0.45);
  const barAreaWidth = width - labelWidth - 20;

  safeItems.forEach((item, index) => {
    const rowY = y + index * rowGap;
    const value = Number(item.value || 0);
    const barWidth = (value / maxValue) * barAreaWidth;
    const color = colors[index % colors.length] || [124, 58, 237];

    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(60, 60, 60);
    const label = sanitizeText(item.label || 'Untitled');
    pdf.text(label.length > 30 ? `${label.slice(0, 27)}...` : label, x, rowY + 4.5);

    pdf.setFillColor(235, 232, 239);
    pdf.rect(x + labelWidth, rowY, barAreaWidth, barHeight, 'F');
    pdf.setFillColor(...color);
    pdf.rect(x + labelWidth, rowY, barWidth, barHeight, 'F');

    pdf.setFontSize(8);
    pdf.setTextColor(45, 45, 45);
    pdf.text(`${value.toFixed(1)}%`, x + labelWidth + barAreaWidth + 2, rowY + 4.5);
  });

  return y + safeItems.length * rowGap + 2;
}



/**
 * Export Comment Banks to PDF with visual progress bars
 */
export async function exportCommentBanksPDF(data, filename, className = null, filterData = null, chartCanvas = null) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  let startY = addHeader(pdf, className, 'Specific Comments', false);

  // Add filter summary if available
  if (filterData) {
    startY = addFilterSummary(pdf, filterData, startY);
  }

  let currentY = startY + 5;

  // Add chart if available
  if (chartCanvas && typeof chartCanvas.toDataURL === 'function') {
    try {
      const imgData = chartCanvas.toDataURL('image/png');
      const canvasWidth = chartCanvas.width || 1;
      const canvasHeight = chartCanvas.height || 1;
      const aspectRatio = canvasWidth / canvasHeight;

      // Use a base width and calculate height to preserve aspect ratio
      const chartWidth = 80;
      const chartHeight = chartWidth / aspectRatio;

      pdf.addImage(imgData, 'PNG', MARGIN, currentY, chartWidth, chartHeight);
      currentY += chartHeight + 6;
    } catch (_error) {
      // Silently skip chart if it fails
    }
  }

  // Process each comment bank
  const banks = Array.isArray(data) ? data : [];

  if (banks.length === 0) {
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text('No comment bank data available', MARGIN, currentY);
  }

  for (const bank of banks) {
    // Check if we need a new page
    if (currentY > PAGE_HEIGHT - 30) {
      pdf.addPage();
      currentY = MARGIN;
    }

    // Draw bank title
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    const bankTitle = sanitizeText(bank.title || bank.name || 'Untitled Comment Bank');
    pdf.text(bankTitle, MARGIN, currentY);
    currentY += 8;

    // Process suggestions
    const suggestions = bank.suggestions || [];
    for (const suggestion of suggestions) {
      // Check if we need a new page
      if (currentY > PAGE_HEIGHT - 20) {
        pdf.addPage();
        currentY = MARGIN;
      }

      // Suggestion title and percentage
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(60, 60, 60);
      const suggestionTitle = sanitizeText(suggestion.title || 'Untitled Suggestion');
      const suggestionPct = `${Number(suggestion.usagePercentage || 0).toFixed(1)}%`;
      pdf.text(`${suggestionTitle} (${suggestionPct})`, MARGIN + 5, currentY);
      currentY += 6;

      // Comments under this suggestion
      const comments = suggestion.comments || [];
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(80, 80, 80);

      for (const comment of comments) {
        if (currentY > PAGE_HEIGHT - 15) {
          pdf.addPage();
          currentY = MARGIN;
        }

        const commentText = sanitizeText(comment.comment || '');
        const commentPct = `${Number(comment.usagePercentage || 0).toFixed(1)}%`;
        const maxWidth = PAGE_WIDTH - MARGIN * 2 - 10;
        const textLines = pdf.splitTextToSize(`• ${commentText} (${commentPct})`, maxWidth);
        pdf.text(textLines, MARGIN + 10, currentY);
        currentY += textLines.length * 4;
      }

      currentY += 3;
    }

    currentY += 5;
  }

  // Add page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(
      `Page ${i} of ${pageCount}`,
      PAGE_WIDTH - MARGIN,
      PAGE_HEIGHT - 5,
      { align: 'right' }
    );
  }

  pdf.save(filename);
}

/**
 * Export Rubrics to PDF (Landscape orientation, multiple tables with actual criterion names)
 */
export async function exportRubricsPDF(data, filename, className = null, filterData = null, chartCanvas = null) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  let startY = addHeader(pdf, className, 'Rubrics', false);

  if (filterData) {
    startY = addFilterSummary(pdf, filterData, startY);
  }

  // Get chunks - array of [headers, ...rows]
  const chunks = transformRubricsToPDF(data, 5);

  let currentY = startY + 5;

  chunks.forEach((chunk, chunkIndex) => {
    const { head, body } = csvToTableData(chunk);

    // Build column styles
    const columnStyles = {};

    head[0]?.forEach((header, index) => {
      const headerStr = String(header || '');
      if (headerStr === 'Rank' || headerStr.includes('Rank')) {
        columnStyles[index] = { cellWidth: 10, halign: 'center' };
      } else if (headerStr === 'Student' || headerStr.includes('Student')) {
        columnStyles[index] = { cellWidth: 30, halign: 'left' };
      } else if (headerStr === 'Question' || headerStr.includes('Question')) {
        columnStyles[index] = { cellWidth: 12, halign: 'center' };
      } else {
        // Task + Criteria columns - use actual names, auto width
        columnStyles[index] = { cellWidth: 'auto', halign: 'center' };
      }
    });

    // Add chunk label if multiple chunks
    if (chunks.length > 1) {
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Part ${chunkIndex + 1} of ${chunks.length}`, MARGIN, currentY);
      currentY += 5;
    }

    autoTable(pdf, {
      head,
      body,
      startY: currentY,
      margin: { left: MARGIN, right: MARGIN },
      tableWidth: 'auto',
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        valign: 'middle',
        halign: 'center',
      },
      columnStyles,
      headStyles: {
        fillColor: [126, 75, 222],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7,
        valign: 'middle',
        halign: 'center',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    currentY = pdf.lastAutoTable.finalY + 10;
  });

  // Add page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    const pageSize = pdf.internal.pageSize;
    const pageHeight = pageSize.height || pageSize.getHeight();
    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageSize.width - MARGIN,
      pageHeight - 5,
      { align: 'right' }
    );
  }

  pdf.save(filename);
}

/**
 * Export Short Answer to PDF
 */
export async function exportShortAnswerPDF(data, filename, className = null, filterData = null) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  let startY = addHeader(pdf, className, 'Short Answer', false);

  // Add filter summary if available
  if (filterData) {
    startY = addFilterSummary(pdf, filterData, startY);
  }

  const csvData = transformShortAnswerToCSV(data);
  const { head, body } = csvToTableData(csvData);

  autoTable(pdf, {
    head,
    body,
    startY: startY + 5,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: [126, 75, 222],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    didDrawPage: (data) => {
      const pageCount = pdf.internal.getNumberOfPages();
      const pageSize = pdf.internal.pageSize;
      const pageHeight = pageSize.height || pageSize.getHeight();
      pdf.setFontSize(8);
      pdf.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageSize.width - MARGIN,
        pageHeight - 5,
        { align: 'right' }
      );
    },
  });

  pdf.save(filename);
}

/**
 * Export Success Criteria to PDF with visual progress bars
 */
export async function exportSuccessCriteriaPDF(data, filename, className = null, filterData = null) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  let startY = addHeader(pdf, className, 'Success Criteria', false);

  // Add filter summary if available
  if (filterData) {
    startY = addFilterSummary(pdf, filterData, startY);
  }

  // Add legend
  const legendY = startY + 2;
  const legendItemWidth = 55;
  let legendX = MARGIN;

  pdf.setFontSize(8);
  pdf.setFont(undefined, 'bold');

  // Satisfied legend
  pdf.setFillColor(27, 168, 15);
  pdf.rect(legendX, legendY, 4, 4, 'F');
  pdf.setTextColor(0, 0, 0);
  pdf.text('Satisfied', legendX + 6, legendY + 3);
  legendX += legendItemWidth;

  // Partially satisfied legend
  pdf.setFillColor(228, 119, 10);
  pdf.rect(legendX, legendY, 4, 4, 'F');
  pdf.text('Partially satisfied', legendX + 6, legendY + 3);
  legendX += legendItemWidth + 10;

  // Not satisfied legend
  pdf.setFillColor(240, 54, 54);
  pdf.rect(legendX, legendY, 4, 4, 'F');
  pdf.text('Not satisfied', legendX + 6, legendY + 3);

  let currentY = legendY + 10;

  // Process each criteria section
  const sections = Array.isArray(data) ? data : [];

  for (const section of sections) {
    // Check if we need a new page
    if (currentY > PAGE_HEIGHT - 30) {
      pdf.addPage();
      currentY = MARGIN;
    }

    // Draw section title
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    const sectionTitle = sanitizeText(section.title || 'Untitled Criteria');
    pdf.text(sectionTitle, MARGIN, currentY);
    currentY += 7;

    // Process objectives
    const objectives = section.objectives || [];
    for (const objective of objectives) {
      // Check if we need a new page
      if (currentY > PAGE_HEIGHT - 25) {
        pdf.addPage();
        currentY = MARGIN;
      }

      // Draw objective title
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(60, 60, 60);

      const maxWidth = PAGE_WIDTH - (MARGIN * 2);
      const objectiveTitle = sanitizeText(objective.title || 'Untitled Objective');
      const titleLines = pdf.splitTextToSize(objectiveTitle, maxWidth);
      pdf.text(titleLines, MARGIN, currentY);
      currentY += titleLines.length * 4 + 2;

      // Extract percentages
      const satisfied = Number(objective.satisfiedPercentage) || 0;
      const partiallySatisfied = Number(objective.partiallySatisfiedPercentage) || 0;
      const notSatisfied = Number(objective.notSatisfiedPercentage) || 0;

      // Draw progress bar
      const barWidth = PAGE_WIDTH - (MARGIN * 2);
      drawSuccessCriteriaBar(pdf, MARGIN, currentY, barWidth, satisfied, partiallySatisfied, notSatisfied);
      currentY += 12;
    }

    currentY += 5; // Space between sections
  }

  // Add page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(
      `Page ${i} of ${pageCount}`,
      PAGE_WIDTH - MARGIN,
      PAGE_HEIGHT - 5,
      { align: 'right' }
    );
  }

  pdf.save(filename);
}

/**
 * Export Summary to PDF
 */
export async function exportSummaryPDF(data, filename, className = null) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  let startY = addHeader(pdf, className, 'Summary & Statistics');

  const csvData = transformSummaryToCSV(data);
  const { head, body } = csvToTableData(csvData);

  autoTable(pdf, {
    head,
    body,
    startY,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize: 10,
      cellPadding: 4,
    },
    headStyles: {
      fillColor: [126, 75, 222],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  });

  pdf.save(filename);
}

/**
 * Export Full Report to PDF (all sections)
 */
/**
 * Add filter summary to PDF showing applied filters
 */
function addFilterSummary(pdf, filterData, startY) {
  if (!filterData) return startY;

  let currentY = startY;
  const leftMargin = MARGIN;
  const lineHeight = 6;
  const labelWidth = 40; // Fixed width for all labels to ensure alignment
  const valueStartX = leftMargin + labelWidth;
  const maxValueWidth = PAGE_WIDTH - valueStartX - MARGIN;

  pdf.setFontSize(12);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(124, 58, 237); // Purple color
  pdf.text('Applied Filters', leftMargin, currentY);
  currentY += lineHeight + 2;

  pdf.setFontSize(9);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(0, 0, 0);

  // Helper function to add filter row with consistent alignment
  const addFilterRow = (label, value) => {
    if (!value) return;

    pdf.setFont(undefined, 'bold');
    pdf.text(label, leftMargin, currentY);
    pdf.setFont(undefined, 'normal');

    const splitValue = pdf.splitTextToSize(value, maxValueWidth);
    pdf.text(splitValue, valueStartX, currentY);
    currentY += lineHeight * splitValue.length;
  };

  // Class names
  if (filterData.classLabels && filterData.classLabels.length > 0) {
    addFilterRow('Classes:', filterData.classLabels.join(', '));
  }

  // Date range
  if (filterData.dateRange) {
    addFilterRow('Date Range:', filterData.dateRange);
  }

  // Comment Banks (marking tool filter)
  if (filterData.commentBankLabels && filterData.commentBankLabels.length > 0) {
    addFilterRow('Comment Banks:', filterData.commentBankLabels.join(', '));
  }

  // Success Criteria (marking tool filter)
  if (filterData.successCriteriaLabels && filterData.successCriteriaLabels.length > 0) {
    addFilterRow('Success Criteria:', filterData.successCriteriaLabels.join(', '));
  }

  // Rubrics (marking tool filter)
  if (filterData.rubricLabels && filterData.rubricLabels.length > 0) {
    addFilterRow('Rubrics:', filterData.rubricLabels.join(', '));
  }

  // Short Answers (marking tool filter)
  if (filterData.shortAnswerLabels && filterData.shortAnswerLabels.length > 0) {
    addFilterRow('Short Answers:', filterData.shortAnswerLabels.join(', '));
  }

  // Subjects
  if (filterData.filters?.subjects && filterData.filters.subjects.length > 0) {
    addFilterRow('Subjects:', filterData.filters.subjects.join(', '));
  }

  // Task Types
  if (filterData.filters?.taskTypes && filterData.filters.taskTypes.length > 0) {
    addFilterRow('Task Types:', filterData.filters.taskTypes.join(', '));
  }

  // Tasks
  if (filterData.filters?.taskIds && filterData.filters.taskIds.length > 0) {
    addFilterRow('Tasks:', filterData.filters.taskIds.join(', '));
  }

  // Students
  if (filterData.filters?.studentIds && filterData.filters.studentIds.length > 0) {
    addFilterRow('Students:', filterData.filters.studentIds.join(', '));
  }

  currentY += 5; // Add spacing after filter summary
  return currentY;
}

export async function exportFullReportDataPDF(allData, filename, filterData = null, chartCanvas = null) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  // Add filter summary on first page
  let startY = 15;
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  const pageWidth = pdf.internal.pageSize.getWidth();
  pdf.text('Class Insights Report', pageWidth / 2, startY, { align: 'center' });
  startY += 10;

  if (filterData) {
    startY = addFilterSummary(pdf, filterData, startY);
  }

  let isFirstSection = true;

  // Summary
  if (allData.submissionsStats) {
    if (!isFirstSection) {
      pdf.addPage();
      startY = MARGIN + 5;
    }

    // Add section title
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Summary & Statistics', MARGIN, startY);
    startY += 8;

    const csvData = transformSummaryToCSV(allData.submissionsStats);
    const { head, body } = csvToTableData(csvData);

    autoTable(pdf, {
      head,
      body,
      startY,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [126, 75, 222], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    isFirstSection = false;
  }

  // Comment Banks
  if ('commentBankStats' in (allData || {}) && allData.commentBankStats && allData.commentBankStats.length > 0) {
    if (!isFirstSection) {
      pdf.addPage();
      startY = MARGIN + 5;
    }

    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Comment Banks', MARGIN, startY);
    startY += 8;

    // Add pie chart if available
    if (chartCanvas && typeof chartCanvas.toDataURL === 'function') {
      try {
        const imgData = chartCanvas.toDataURL('image/png', 1.0);
        const canvasWidth = chartCanvas.width || 1;
        const canvasHeight = chartCanvas.height || 1;
        const aspectRatio = canvasWidth / canvasHeight;

        // Chart dimensions
        const chartWidth = 100;
        const chartHeight = chartWidth / aspectRatio;

        // Center the chart
        const chartX = (pdf.internal.pageSize.getWidth() - chartWidth) / 2;

        pdf.addImage(imgData, 'PNG', chartX, startY, chartWidth, chartHeight);
        startY = startY + chartHeight + 10;
      } catch (error) {
        console.error('❌ [PDF Export] Error adding Comment Bank chart:', error);
      }
    }

    // Render comment banks hierarchically - group by bank and suggestion
    let currentY = startY;
    const rawData = Array.isArray(allData.commentBankStats) ? allData.commentBankStats : [];
    const pageHeight = pdf.internal.pageSize.getHeight();

    const isFlat = rawData.length > 0 && rawData[0]['Comment Bank'] !== undefined;

    if (isFlat) {
      const bankMap = new Map();

      rawData.forEach(row => {
        const bankName = row['Comment Bank'] || 'Untitled Bank';
        const suggestionName = row['Suggestion'] || 'Untitled';
        const suggestionPct = row['Suggestion %'] || '0.0%';
        const comment = row['Comment'] || '';
        const commentPct = row['Comment %'] || '0.0%';

        if (!bankMap.has(bankName)) {
          bankMap.set(bankName, new Map());
        }
        const suggestionMap = bankMap.get(bankName);

        if (!suggestionMap.has(suggestionName)) {
          suggestionMap.set(suggestionName, { percentage: suggestionPct, comments: [] });
        }
        if (comment && comment !== 'No comments') {
          suggestionMap.get(suggestionName).comments.push({ text: comment, percentage: commentPct });
        }
      });

      for (const [bankName, suggestionMap] of bankMap) {
        if (currentY > pageHeight - 30) {
          pdf.addPage();
          currentY = MARGIN;
        }

        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(sanitizeText(bankName), MARGIN, currentY);
        currentY += 8;

        for (const [suggestionName, suggestionData] of suggestionMap) {
          if (currentY > pageHeight - 20) {
            pdf.addPage();
            currentY = MARGIN;
          }

          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(60, 60, 60);
          pdf.text(`${sanitizeText(suggestionName)} (${suggestionData.percentage})`, MARGIN + 5, currentY);
          currentY += 6;

          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(80, 80, 80);

          for (const comment of suggestionData.comments) {
            if (currentY > pageHeight - 15) {
              pdf.addPage();
              currentY = MARGIN;
            }

            const maxWidth = pdf.internal.pageSize.getWidth() - MARGIN * 2 - 10;
            const textLines = pdf.splitTextToSize(`• ${sanitizeText(comment.text)} (${comment.percentage})`, maxWidth);
            pdf.text(textLines, MARGIN + 10, currentY);
            currentY += textLines.length * 4;
          }

          currentY += 3;
        }

        currentY += 5;
      }
    } else {
      for (const bank of rawData) {
        if (currentY > pageHeight - 30) {
          pdf.addPage();
          currentY = MARGIN;
        }

        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        const bankTitle = sanitizeText(bank.title || bank.name || 'Untitled Comment Bank');
        pdf.text(bankTitle, MARGIN, currentY);
        currentY += 8;

        const suggestions = bank.suggestions || [];
        for (const suggestion of suggestions) {
          if (currentY > pageHeight - 20) {
            pdf.addPage();
            currentY = MARGIN;
          }

          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(60, 60, 60);
          const suggestionTitle = sanitizeText(suggestion.title || 'Untitled Suggestion');
          const suggestionPct = `${Number(suggestion.usagePercentage || 0).toFixed(1)}%`;
          pdf.text(`${suggestionTitle} (${suggestionPct})`, MARGIN + 5, currentY);
          currentY += 6;

          const comments = suggestion.comments || [];
          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(80, 80, 80);

          for (const comment of comments) {
            if (currentY > pageHeight - 15) {
              pdf.addPage();
              currentY = MARGIN;
            }

            const commentText = sanitizeText(comment.comment || '');
            const commentPct = `${Number(comment.usagePercentage || 0).toFixed(1)}%`;
            const maxWidth = pdf.internal.pageSize.getWidth() - MARGIN * 2 - 10;
            const textLines = pdf.splitTextToSize(`• ${commentText} (${commentPct})`, maxWidth);
            pdf.text(textLines, MARGIN + 10, currentY);
            currentY += textLines.length * 4;
          }

          currentY += 3;
        }

        currentY += 5;
      }
    }

    isFirstSection = false;
  }

  // Success Criteria
  if ('successCriteriaStats' in (allData || {}) && allData.successCriteriaStats && allData.successCriteriaStats.length > 0) {
    if (!isFirstSection) {
      pdf.addPage();
      startY = MARGIN + 5;
    } else {
      startY = pdf.lastAutoTable?.finalY + 10 || startY;
    }

    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Success Criteria', MARGIN, startY);
    startY += 8;

    const csvData = transformSuccessCriteriaToCSV(allData.successCriteriaStats);
    const { head, body } = csvToTableData(csvData);

    autoTable(pdf, {
      head,
      body,
      startY,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [126, 75, 222], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    isFirstSection = false;
  }

  // Rubrics
  if ('rubricsStats' in (allData || {}) && allData.rubricsStats && allData.rubricsStats.length > 0) {
    if (!isFirstSection) {
      pdf.addPage();
      startY = MARGIN + 5;
    } else {
      startY = pdf.lastAutoTable?.finalY + 10 || startY;
    }

    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Rubrics', MARGIN, startY);
    startY += 8;

    const chunks = transformRubricsToPDF(allData.rubricsStats, 5);
    let currentY = startY;

    chunks.forEach((chunk, chunkIndex) => {
      const { head, body } = csvToTableData(chunk);

      if (chunks.length > 1) {
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Part ${chunkIndex + 1} of ${chunks.length}`, MARGIN, currentY);
        currentY += 5;
      }

      autoTable(pdf, {
        head,
        body,
        startY: currentY,
        margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', valign: 'middle', halign: 'center' },
        headStyles: { fillColor: [126, 75, 222], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;
    });

    isFirstSection = false;
  }

  // Short Answer
  const hasShortAnswerData = allData.shortAnswerStats && (
    (Array.isArray(allData.shortAnswerStats) && allData.shortAnswerStats.length > 0) ||
    (allData.shortAnswerStats.tasks && Array.isArray(allData.shortAnswerStats.tasks) && allData.shortAnswerStats.tasks.length > 0)
  );

  if (hasShortAnswerData) {
    if (!isFirstSection) {
      pdf.addPage();
      startY = MARGIN + 5;
    } else {
      startY = pdf.lastAutoTable?.finalY + 10 || startY;
    }

    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Short Answer Responses', MARGIN, startY);
    startY += 8;

    const csvData = transformShortAnswerToCSV(allData.shortAnswerStats);
    const { head, body } = csvToTableData(csvData);

    const hasActualData = body.length > 0 && body[0][0] !== 'No data available';
    if (hasActualData) {
      autoTable(pdf, {
        head,
        body,
        startY,
        margin: { left: MARGIN, right: MARGIN },
        styles: { fontSize: 9, cellPadding: 3, cellWidth: 'wrap' },
        headStyles: { fillColor: [126, 75, 222], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
      isFirstSection = false;
    }
  }

  // Add page numbers to all pages
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    const pageSize = pdf.internal.pageSize;
    const pageHeight = pageSize.height || pageSize.getHeight();
    pdf.text(
      `Page ${i} of ${pageCount}`,
      pageSize.width - MARGIN,
      pageHeight - 5,
      { align: 'right' }
    );
  }

  pdf.save(filename);
}

/**
 * Export generic data to PDF (for pre-transformed CSV data)
 */
export async function exportGenericDataPDF(csvData, filename, sectionTitle, className = null) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const sanitizedClassName = className ? sanitizeText(className) : null;
  const sanitizedSectionTitle = sectionTitle ? sanitizeText(sectionTitle) : null;

  let startY = addHeader(pdf, sanitizedClassName, sanitizedSectionTitle);
  const { head, body } = csvToTableData(csvData);

  autoTable(pdf, {
    head,
    body,
    startY,
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [126, 75, 222],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
    },
    didDrawPage: (data) => {
      const pageCount = pdf.internal.getNumberOfPages();
      const pageSize = pdf.internal.pageSize;
      const pageHeight = pageSize.height || pageSize.getHeight();
      pdf.setFontSize(8);
      pdf.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageSize.width - MARGIN,
        pageHeight - 5,
        { align: 'right' }
      );
    },
  });

  pdf.save(filename);
}

/**
 * Export section based on section type
 * @param {string} section - Section type
 * @param {*} data - Raw data (may not be used if csvData is provided)
 * @param {string} filename - Output filename
 * @param {string} className - Optional class name
 * @param {Array} csvData - Pre-transformed CSV data (if available)
 */
export async function exportSectionDataPDF(section, data, filename, className = null, csvData = null) { 
  if (csvData && Array.isArray(csvData) && csvData.length > 0 && section !== 'rubrics') {
    const sectionTitles = {
      'comment-banks': 'Comment Banks',
      'success-criteria': 'Success Criteria',
      'rubrics': 'Rubrics',
      'short-answer': 'Short Answer Responses',
      'summary': 'Summary & Statistics',
    };

    const sectionTitle = sectionTitles[section] || section;
    return exportGenericDataPDF(csvData, filename, sectionTitle, className);
  }

  switch (section) {
    case 'comment-banks':
      await exportCommentBanksPDF(data, filename, className);
      break;
    case 'success-criteria':
      await exportSuccessCriteriaPDF(data, filename, className);
      break;
    case 'rubrics':
      await exportRubricsPDF(data, filename, className);
      break;
    case 'short-answer':
      await exportShortAnswerPDF(data, filename, className);
      break;
    case 'summary':
      await exportSummaryPDF(data, filename, className);
      break;
    default:
      throw new Error(`Unknown section: ${section}`);
  }
}
